import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.css';
import AutoSave from 'stimulus/auto_save';

// Tom Select stands in for the React ProxyAutocomplete: it throttles the query,
// draws the dropdown, handles arrow keys / Enter / Escape / click-outside and
// discards free text that was never picked from the list. The predictions and
// the place details still come from PlacesController.
const AUTOCOMPLETE_URL = '/places/autocomplete';
const DETAILS_URL = '/places/details';
const MIN_QUERY_LENGTH = 2;

export default class extends Controller {
  static targets = ['input', 'hidden', 'message'];

  static values = {
    autoSaveUrl: String,
    messageId: String,
    types: { type: String, default: '(cities)' },
    debounce: { type: Number, default: 50 }
  };

  connect() {
    // Turbo snapshots the page as-is; destroy() restores the original input so
    // the cached copy re-enhances cleanly on restore.
    this.handleBeforeCache = () => this.teardown();
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);

    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      // React read `errors.city || errors.country_code` off the response.
      name: ['city', 'country_code'],
      debounce: this.debounceValue,
      label: this.identifier,
      onSaved: () => this.dispatch('saved'),
      onError: message => this.showError(message)
    });

    this.tomSelect = new TomSelect(this.inputTarget, this.settings());
    // The original input is hidden from assistive tech once enhanced, so the
    // state it carries has to reach the input Tom Select renders.
    this.setInvalid(this.inputTarget.getAttribute('aria-invalid') === 'true');
    this.describeByMessage(this.inputTarget.hasAttribute('aria-describedby'));
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
    this.autoSave.cancel();
    this.teardown();
  }

  // ── Public actions (wired from data-action) ──────────────────────────────
  // React cleared the error on any keystroke inside the field.
  handleInput() {
    this.clearError();
  }

  // ── Tom Select ────────────────────────────────────────────────────────────
  settings() {
    return {
      maxItems: 1,
      maxOptions: null,
      create: false,
      // Predictions arrive already filtered by Google; local sifting would
      // hide rows whose text does not literally contain what was typed.
      searchField: [],
      // React only reopened the dropdown when predictions were already loaded,
      // so re-focusing a field that holds a pick shows nothing.
      openOnFocus: false,
      loadThrottle: 300,
      placeholder: this.inputTarget.placeholder,
      shouldLoad: query => query.trim().length >= MIN_QUERY_LENGTH,
      load: (query, callback) => this.loadPredictions(query, callback),
      onChange: value => this.change(value),
      // The stock `no_results` row already reads "No results found" and only
      // shows once `shouldLoad` passes — React's condition exactly. `loading`
      // defaults to a spinner, where React showed a row of text.
      render: {
        option: data => this.renderOption(data),
        loading: () => '<div class="loading">Loading...</div>'
      },
      ...this.initialSelection()
    };
  }

  // Tom Select seeds a text input's own value as an option, but splits it on
  // its delimiter first — which would turn "Lisbon, Portugal" into two items.
  // Passing the pair explicitly keeps the server-rendered string whole.
  initialSelection() {
    const display = this.inputTarget.value;
    if (!display) return {};

    return { options: [{ value: display, text: display }], items: [display] };
  }

  renderOption(data) {
    const option = document.createElement('div');

    if (!data.mainText) {
      option.textContent = data.text;
      return option;
    }

    const main = document.createElement('span');
    main.className = 'ui-location-field__option-main';
    main.textContent = data.mainText;

    const secondary = document.createElement('span');
    secondary.className = 'ui-location-field__option-secondary';
    secondary.textContent = data.secondaryText || '';

    option.append(main, secondary);
    return option;
  }

  async loadPredictions(query, callback) {
    try {
      const url = new URL(AUTOCOMPLETE_URL, window.location.origin);
      url.searchParams.set('input', query);
      url.searchParams.set('types', this.typesValue);

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(response.statusText);

      const { predictions } = await response.json();
      callback((predictions || []).map(prediction => this.toOption(prediction)));
    } catch (error) {
      console.warn(`[${this.identifier}] could not load predictions`, error); // eslint-disable-line no-console
      callback();
    }
  }

  // The description doubles as the option value so the visible input keeps
  // submitting the same "Lisbon, Portugal" string the React field did.
  toOption(prediction) {
    const formatting = prediction.structured_formatting;

    return {
      value: prediction.description,
      text: prediction.description,
      placeId: prediction.place_id,
      mainText: formatting && formatting.main_text,
      secondaryText: formatting && formatting.secondary_text
    };
  }

  // ── Location ──────────────────────────────────────────────────────────────
  // Fires on a pick and on a clear — Tom Select reports '' once the item is
  // gone — so it covers React's handleChange and its blur-time reset.
  async change(value) {
    this.clearError();

    if (!value) {
      this.writeLocation({});
      this.dispatch('reset');
      return;
    }

    const place = await this.fetchPlace(this.tomSelect.options[value]);
    // React ignored details without address components: nothing changed.
    if (!place || !place.address_components) return;

    const location = this.locationFrom(place);
    this.writeLocation(location);
    this.dispatch('change', { detail: { location, place } });

    if (this.autoSaveUrlValue) this.autoSave.save(this.locationFormData());
  }

  async fetchPlace(option) {
    if (!option || !option.placeId) return null;

    try {
      const url = new URL(DETAILS_URL, window.location.origin);
      url.searchParams.set('place_id', option.placeId);

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(response.statusText);

      const { result } = await response.json();
      return result || null;
    } catch (error) {
      console.warn(`[${this.identifier}] could not load place details`, error); // eslint-disable-line no-console
      return null;
    }
  }

  // Mirrors LocationInput.handleChange: without a country nothing is kept, and
  // the city and coordinates only come along for a locality with geometry.
  locationFrom(place) {
    const components = place.address_components;
    const country = components.find(component => component.types.includes('country'));
    const city = components.find(component => component.types.includes('locality'));
    const location = {};
    if (!country) return location;

    location.country_code = country.short_name;
    location.google_place_id = place.place_id;

    if (place.geometry && city) {
      location.city = city.long_name;
      location.latitude = String(place.geometry.location.lat);
      location.longitude = String(place.geometry.location.lng);
    }

    return location;
  }

  writeLocation(location) {
    this.hiddenTargets.forEach(input => {
      input.value = location[input.dataset.locationFieldField] || '';
    });
  }

  // React posted every hidden input in the field, not just the changed one.
  locationFormData() {
    const formData = new FormData();
    this.hiddenTargets.forEach(input => formData.append(input.name, input.value));

    return formData;
  }

  // ── Error / hint region ───────────────────────────────────────────────────
  // The hint is a Ui::HintComponent; its text lives in the `[data-hint-text]`
  // span and the error icon/colour swap is CSS-driven by the wrapper's
  // `--error` modifier.
  showError(message) {
    this.element.classList.add('ui-location-field--error');
    this.setInvalid(true);
    if (!this.hasMessageTarget) return;

    this.setMessageText(message);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.hidden = false;
    this.describeByMessage(true);
  }

  clearError() {
    if (!this.element.classList.contains('ui-location-field--error')) return;

    this.element.classList.remove('ui-location-field--error');
    this.setInvalid(false);
    if (!this.hasMessageTarget) return;

    // Server-rendered errors carry the variant class and role on the hint
    // itself; drop them so the restored hint renders as a neutral hint.
    this.messageTarget.classList.remove('ui-hint--error');
    this.messageTarget.removeAttribute('role');
    const hint = this.element.dataset.locationFieldHint || '';
    this.setMessageText(hint);
    this.messageTarget.hidden = hint === '';
    this.describeByMessage(hint !== '');
  }

  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  setInvalid(invalid) {
    this.ariaInputs().forEach(input => input.setAttribute('aria-invalid', String(invalid)));
  }

  // React rebuilt `aria-describedby` from the regions it rendered, so the
  // message id must come and go with the visible message.
  describeByMessage(present) {
    this.ariaInputs().forEach(input => {
      const ids = (input.getAttribute('aria-describedby') || '')
        .split(' ')
        .filter(id => id && id !== this.messageIdValue);

      if (present) ids.unshift(this.messageIdValue);

      if (ids.length) {
        input.setAttribute('aria-describedby', ids.join(' '));
      } else {
        input.removeAttribute('aria-describedby');
      }
    });
  }

  // The submitted input plus, once enhanced, the one the user actually types in.
  ariaInputs() {
    return [this.inputTarget, this.tomSelect && this.tomSelect.control_input].filter(Boolean);
  }

  teardown() {
    this.tomSelect?.destroy();
    this.tomSelect = null;
  }
}
